import React from "react";
import { api, API_BASE } from "@/lib/api";
import { Briefcase, MapPin, Clock, ArrowRight, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { CAREER } from "@/constants/testIds";

export default function Careers() {
  const [jobs, setJobs] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", cover_letter: "" });
  const [resume, setResume] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    api.get("/jobs?active_only=true").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const openApply = (job) => {
    setSelected(job);
    setForm({ name: "", email: "", phone: "", cover_letter: "" });
    setResume(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) { toast.error("Please attach your resume"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_id", selected.id);
      fd.append("job_title", selected.title);
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("cover_letter", form.cover_letter);
      fd.append("resume", resume);
      await fetch(`${API_BASE}/applications`, { method: "POST", body: fd });
      toast.success("Application submitted. We'll be in touch shortly.");
      setSelected(null);
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="hero-mesh grain">
        <div className="container-wide py-20 md:py-28">
          <div className="overline">Careers at Skyverra</div>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">Build the software companies actually run on.</h1>
          <p className="mt-6 max-w-2xl text-[color:var(--sky-muted)] text-lg leading-relaxed">
            Join a team where senior engineers lead, decisions ship fast, and every project touches real
            enterprise customers. Remote-first, outcome-driven, and generous with growth.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-wide">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="overline">Open positions</div>
              <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl tracking-tight">Currently hiring · {jobs.length} role{jobs.length === 1 ? "" : "s"}</h2>
            </div>
          </div>

          {jobs.length === 0 && (
            <div className="card-flat p-10 text-center text-[color:var(--sky-muted)]">
              No open positions right now. Send your resume to <a className="text-[color:var(--sky-navy)] font-semibold" href="mailto:info@skyverraconsulting.net">info@skyverraconsulting.net</a> and we&apos;ll keep you in mind.
            </div>
          )}

          <div className="grid gap-4">
            {jobs.map((j) => (
              <div key={j.id} data-testid={CAREER.jobCard(j.id)} className="card-flat p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="badge-cyan">{j.department || "Team"}</span>
                    <span className="badge-cyan" style={{background:"rgba(26,76,139,0.08)"}}>{j.employment_type}</span>
                  </div>
                  <h3 className="mt-3 font-display font-bold text-2xl tracking-tight">{j.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--sky-muted)]">{j.description}</p>
                  <div className="mt-4 flex flex-wrap gap-4 text-xs text-[color:var(--sky-muted)]">
                    <div className="inline-flex items-center gap-1.5"><MapPin size={13} /> {j.location || "Remote"}</div>
                    <div className="inline-flex items-center gap-1.5"><Briefcase size={13} /> {j.experience || "-"}</div>
                    <div className="inline-flex items-center gap-1.5"><Clock size={13} /> Posted {new Date(j.posted_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <button onClick={() => openApply(j)} data-testid={CAREER.applyBtn(j.id)} className="btn-primary text-sm shrink-0">
                  Apply now <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-md max-w-xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="p-6 border-b border-[color:var(--sky-border)] flex items-start justify-between">
              <div>
                <div className="overline">Apply for</div>
                <h3 className="mt-1 font-display font-bold text-2xl">{selected.title}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-[color:var(--sky-muted)] hover:text-[color:var(--sky-ink)]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} data-testid={CAREER.applyForm} className="p-6 grid gap-4">
              <div className="grid gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-[color:var(--sky-muted)]">Full name</label>
                <input required data-testid={CAREER.applyName} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-[color:var(--sky-muted)]">Email</label>
                <input required type="email" data-testid={CAREER.applyEmail} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="grid gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-[color:var(--sky-muted)]">Phone</label>
                <input data-testid={CAREER.applyPhone} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="grid gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-[color:var(--sky-muted)]">Cover note</label>
                <textarea rows={4} data-testid={CAREER.applyCover} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} />
              </div>
              <div className="grid gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-[color:var(--sky-muted)]">Resume (PDF, DOC, DOCX)</label>
                <label className="cursor-pointer flex items-center gap-3 border border-dashed border-[color:var(--sky-border)] px-4 py-3 rounded-sm hover:border-[color:var(--sky-cyan)]">
                  <Upload size={16} className="text-[color:var(--sky-navy)]" />
                  <span className="text-sm">{resume ? resume.name : "Choose a file to upload"}</span>
                  <input data-testid={CAREER.applyResume} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files[0])} />
                </label>
              </div>
              <button disabled={submitting} data-testid={CAREER.applySubmit} className="btn-primary mt-2 justify-center disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit application"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
